import { Feature, Point } from 'geojson';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { GeoJSONSource } from 'mapbox-gl';
import { Layer, type MapMouseEvent } from 'react-map-gl/mapbox';
import { COLOR_TEXT_PRIMARY_INVERSE, FONT_SIZE_100, FONT_SIZE_50 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateCursor } from '../../redux/reducer';
import { defaultSymbolLayerLayoutProps, getLayerBeforeId, trailClustersId } from '../../utils/layers';
import trailClusterHoveredSvg from '../../utils/svgDefinitions/trailClusterHoveredSvg';
import trailClusterSvg from '../../utils/svgDefinitions/trailClusterSvg';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';

const svgDefinitions: SvgDefinition[] = [trailClusterSvg, trailClusterHoveredSvg];

export type TrailClustersProps = {
  sourceId: string;
};

const TrailClusters = ({ sourceId }: TrailClustersProps) => {
  const map = useMap();
  const [hoveredClusterId, setHoveredClusterId] = useState(); // This is outside of redux because as of now, we have no reason to know this outside of this component
  const dispatch = useAppDispatch();
  const { allowClickEvents } = useAppSelector(state => ({ allowClickEvents: state.map.allowClickEvents }));

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  // On cluster click, zoom the map into the point that the cluster would break apart
  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        const cluster = event.features[0] as Feature<Point>;
        const clusterId = cluster.properties?.cluster_id;
        if (clusterId) {
          (event.target.getSource(sourceId) as GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) {
              return;
            }

            event.target.easeTo({ center: cluster.geometry.coordinates as [number, number], zoom: zoom ?? undefined });
          });
        }
      }
    },
    [sourceId]
  );

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        const clusterId = event.features[0]?.properties?.cluster_id;
        if (clusterId) {
          dispatch(updateCursor('pointer'));
          setHoveredClusterId(clusterId);
        }
      }
    },
    [dispatch]
  );

  const onMouseLeave = useCallback(() => {
    dispatch(updateCursor('unset'));
    setHoveredClusterId(undefined);
  }, [dispatch]);

  useEffect(() => {
    if (!allowClickEvents) {
      return;
    }

    registerEventHandler(trailClustersId, 'click', onClick);
    registerEventHandler(trailClustersId, 'mousemove', onMouseMove);
    registerEventHandler(trailClustersId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(trailClustersId, 'click', onClick);
      unregisterEventHandler(trailClustersId, 'mousemove', onMouseMove);
      unregisterEventHandler(trailClustersId, 'mouseleave', onMouseLeave);
    };
  }, [allowClickEvents, onClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <Layer
      source={sourceId}
      type="symbol"
      id={trailClustersId}
      beforeId={getLayerBeforeId(map, trailClustersId)}
      filter={['has', 'clusterTotalCount']}
      layout={{
        ...defaultSymbolLayerLayoutProps,
        'text-size': ['step', ['get', 'clusterTotalCount'], FONT_SIZE_100, 100, FONT_SIZE_50, 1000, 8], // Reduce the font size at 100 and 1000 to avoid overflow
        'text-anchor': 'center',
        'text-field': '{clusterTotalCount}',
        'icon-image': ['case', ['==', ['get', 'cluster_id'], hoveredClusterId || null], trailClusterHoveredSvg.name, trailClusterSvg.name]
      }}
      paint={{ 'text-color': COLOR_TEXT_PRIMARY_INVERSE }}
    />
  );
};

export default TrailClusters;
